import { SELECTED_STUDIO_ID_NAME } from "./useSelectStudio";
import Cookies from "js-cookie";
import useSignOut from "react-auth-kit/hooks/useSignOut";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import useIsAuthenticated from "react-auth-kit/hooks/useIsAuthenticated";
import { getCurrentUserRole } from "../helpers/userHelper";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useFreshUserProfile } from "./useFreshUserProfile";

export interface IUserModel {
  id: number;
  role: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  isUnreliable: boolean;
  fontSizePercent: number;
  authToken: string;
  permissions: string[];
  isDeleted?: boolean;
  email?: string;
  birthDate?: string;
  profilePhotoUrl?: string;
  /** Set when a global admin opened this profile from the admin panel: the admin's name. */
  impersonatedBy?: string;
}

export const useAuthentication = () => {
  const signOut = useSignOut();
  const currentUser = useAuthUser() as IUserModel;
  const isAuthenticated = useIsAuthenticated();
  const [permissions, setPermissions] = useState<string[]>([]);
  const prevUser = usePrevious(currentUser);
  const [didExpire, setDidExpire] = useState(false);
  const queryClient = useQueryClient();

  // Fetch fresh user profile to ensure up-to-date data (isUnreliable, permissions, etc.)
  const { freshUserData } = useFreshUserProfile(isAuthenticated);

  function usePrevious<T>(value: T): T | undefined {
    const [prev, setPrev] = useState<T>();
    useEffect(() => {
      setPrev(value);
    }, [value]);
    return prev;
  }

  const logout = () => {
    try {
      localStorage.removeItem("permissions");
    } catch {
      // SecurityError in private browsing mode - continue without clearing storage
    }
    setPermissions([]);
    signOut();
  };

  useEffect(() => {
    const isNowLoggedOut = !currentUser && prevUser;

    if (isNowLoggedOut) {
      queryClient.clear();
      setDidExpire(true);
      try {
        localStorage.removeItem("permissions");
      } catch {
        // SecurityError in private browsing mode - continue without clearing storage
      }
      setPermissions([]);
    } else if (currentUser) {
      setDidExpire(false);
      try {
        setPermissions(JSON.parse(localStorage.getItem("permissions") ?? "[]"));
      } catch {
        // SecurityError in private browsing mode - use empty permissions
        setPermissions([]);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (didExpire) {
      setDidExpire(false);
    }
  }, [didExpire]);

  // Auto-logout deleted users
  useEffect(() => {
    if (freshUserData?.isDeleted === true && currentUser) {
      logout();
    }
  }, [freshUserData?.isDeleted, currentUser]);

  // Merge JWT token data with fresh API data
  // Fresh data takes precedence for isUnreliable, permissions, and fontSizePercent
  const userWithPermissions = {
    ...currentUser,
    permissions,
    ...(freshUserData && {
      isUnreliable: freshUserData.isUnreliable ?? currentUser?.isUnreliable,
      permissions: freshUserData.permissions ?? permissions,
      fontSizePercent: freshUserData.fontSizePercent ?? currentUser?.fontSizePercent ?? 100,
    }),
  };

  return {
    isAuthenticated,
    logout,
    currentUser: userWithPermissions,
    currentUserRole: getCurrentUserRole(userWithPermissions),
  };
};

export const getSelectedStudioId = () => {
  return Cookies.get(SELECTED_STUDIO_ID_NAME);
};
